import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { jwtDecode } from 'jwt-decode';
import { AuthService } from '../auth/auth0.service';
import { AuthStore } from '../store/auth.store';
import { ANONYMOUS_USER, PERMISSION_ROLE_MAP, Role, User } from '../models/user.model';
import { environment } from '../../../environments/environment';

interface JwtPayload {
  permissions?: string[];
  exp: number;
}

/** Extract local roles + access strings from OAuth2 permission claims. */
function extractRoles(permissions: string[]): { roles: Role[]; accesses: string[] } {
  const roles: Role[] = [];
  const accesses: string[] = [];
  for (const p of permissions) {
    const role = PERMISSION_ROLE_MAP[p];
    if (role !== undefined) {
      roles.push(role);
      accesses.push(p);
    }
  }
  return { roles, accesses };
}

// The post-callback navigation supersedes the one that triggered the Auth0 bootstrap, but the
// superseded guard keeps running: share one session build so they don't race on token popups.
let pendingUser: Promise<User> | null = null;

function buildUser(authService: AuthService): Promise<User> {
  pendingUser ??= fetchUser(authService).finally(() => (pendingUser = null));
  return pendingUser;
}

async function fetchUser(authService: AuthService): Promise<User> {
  const [cortexToken, u] = await Promise.all([
    firstValueFrom(authService.getToken$(environment.cortexOAuth2Audience, environment.cortexOAuth2Scopes)),
    firstValueFrom(authService.getUser$()),
  ]);
  if (!u) {
    throw new Error('Auth0 session is valid but returned no user profile');
  }
  const iobaseToken = await firstValueFrom(
    authService.getToken$(environment.iobaseIndabaOAuth2Audience, environment.cortexOAuth2Scopes, 'Iobase')
  );

  const cortexPayload = jwtDecode<JwtPayload>(cortexToken);
  const iobasePayload = jwtDecode<JwtPayload>(iobaseToken);
  const { roles, accesses } = extractRoles(cortexPayload.permissions ?? []);

  return {
    email: u.email ?? '',
    familyName: u.family_name ?? '',
    givenName: u.given_name ?? '',
    picture: u.picture,
    fullName: `${u.family_name} ${u.given_name}`,
    roles,
    accesses,
    bearer: cortexToken,
    bearerExp: cortexPayload.exp,
    iobaseBearer: iobaseToken,
    iobaseBearerExp: iobasePayload.exp,
  };
}

export const authGuard: CanActivateFn = async (_route, state) => {
  const store = inject(AuthStore);

  // TEMPORARY: returns before `inject(AuthService)`, which would otherwise build the
  // Auth0 clients and call a tenant that has no application registered yet.
  if (environment.authDisabled) {
    store.login(ANONYMOUS_USER);
    return true;
  }

  const authService = inject(AuthService);
  const router = inject(Router);

  try {
    // 1. Fast path: cached user with a still-valid bearer token
    const storedUser = store.loadStoredUser();
    if (storedUser && store.isSessionValid(storedUser)) {
      store.login(storedUser);
      return true;
    }

    // 2. No valid cache → check Auth0 session. Wait for the initial bootstrap
    //    first so we don't redirect to Auth0 while a redirect callback
    //    (`/?code=...&state=...`) is still being processed.
    await authService.ready;
    const isAuthenticated = await authService.checkAuth();

    if (!isAuthenticated) {
      authService.login(state.url);
      return false;
    }

    // 3. Fetch tokens + profile, decode permissions → roles/accesses
    store.login(await buildUser(authService)); // persists to localStorage + updates signal state
    return true;
  } catch (error) {
    console.error('Auth guard error:', error);
    // A UrlTree (unlike an imperative navigate) is dropped if this navigation was superseded.
    return router.createUrlTree(['/login']);
  }
};
